import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  ShieldCheck,
  Globe,
  FileText,
  ChevronLeft,
  ChevronRight,
  Users,
  Database,
  Shield,
} from "lucide-react";

const CONTACT_PHONE = "+966533546607";

const SECTION_ICONS = [
  <Database className="w-8 h-8" />,
  <FileText className="w-8 h-8" />,
  <ShieldCheck className="w-8 h-8" />,
  <Globe className="w-8 h-8" />,
];

export default function Privacy() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [activeSection, setActiveSection] = useState(0);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printableRef = useRef(null);

  const sections = t("privacy.sections", { returnObjects: true }).map(
    (s, i) => ({
      ...s,
      icon: SECTION_ICONS[i],
    }),
  );

  const nextSection = () => {
    setActiveSection((prev) => (prev + 1) % sections.length);
  };

  const prevSection = () => {
    setActiveSection((prev) => (prev - 1 + sections.length) % sections.length);
  };

  const downloadPolicy = async () => {
    if (isGeneratingPdf || !printableRef.current) return;
    setIsGeneratingPdf(true);
    try {
      // Rendered via html2canvas (not jsPDF's text API) because jsPDF's
      // built-in fonts have no Arabic glyphs and can't shape/reorder Arabic
      // text — screenshotting the browser-rendered DOM sidesteps both issues.
      const canvas = await html2canvas(printableRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL("image/png");

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("baraka-privacy-policy.pdf");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-subtle text-dark-grey pt-32 pb-20 px-6 sm:px-10 md:px-20">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-6xl mx-auto text-center mb-20"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5, type: "spring" }}
          className="w-20 h-20 bg-gradient-to-br from-primary to-medium-blue rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary/30"
        >
          <Shield className="w-10 h-10 text-white" />
        </motion.div>
        <h1 className="text-4xl md:text-6xl font-black text-dark-grey mb-6 leading-tight">
          {t("privacy.title")}
        </h1>
        <div className="w-32 h-2 bg-gradient-to-l from-primary to-medium-blue mx-auto mb-6 rounded-full"></div>
        <p className="text-xl text-grey max-w-3xl mx-auto leading-relaxed">
          {t("privacy.subtitlePrefix")}{" "}
          <span className="font-bold text-primary">
            {t("privacy.subtitleBrand")}
          </span>{" "}
          {t("privacy.subtitleSuffix")}
        </p>
      </motion.div>

      {/* Desktop Grid Layout */}
      <div className="hidden lg:block max-w-7xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 gap-8"
        >
          {sections.map((section, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white rounded-xl shadow-card hover:shadow-xl transition-all duration-500 border border-transparent hover:border-primary/20 overflow-hidden"
            >
              <div className="flex items-start gap-8 p-8">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary to-medium-blue rounded-xl flex items-center justify-center shadow-lg shadow-primary/30 text-white">
                      {section.icon}
                    </div>
                    <div className="absolute -bottom-2 -start-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>
                  </div>
                </div>

                <div className="flex-grow [text-align:start]">
                  <h2 className="text-3xl font-bold text-dark-grey mb-3">
                    {section.title}
                  </h2>
                  <div className="flex flex-wrap gap-2 justify-start mb-4">
                    {section.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  <p className="text-grey leading-loose text-lg whitespace-pre-line mb-4">
                    {section.content}
                  </p>

                  <div className="flex flex-row-reverse items-center gap-4 mt-6">
                    <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                    <span className="text-sm text-grey">
                      {t("privacy.lastUpdated")}: {new Date().getFullYear()}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Mobile Carousel Layout */}
      <div className="lg:hidden max-w-2xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRTL ? -50 : 50 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl shadow-card overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-medium-blue rounded-xl flex items-center justify-center shadow-lg shadow-primary/30 text-white">
                  {sections[activeSection].icon}
                </div>
                <div className="[text-align:end]">
                  <span className="text-sm text-grey">
                    {activeSection + 1} / {sections.length}
                  </span>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-dark-grey [text-align:start] mb-4">
                {sections[activeSection].title}
              </h2>

              <div className="flex flex-wrap gap-2 justify-end mb-4">
                {sections[activeSection].features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                  >
                    {feature}
                  </span>
                ))}
              </div>

              <p className="text-grey leading-loose [text-align:start] whitespace-pre-line">
                {sections[activeSection].content}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Carousel Controls */}
        <div className="flex justify-center items-center gap-6 mt-8">
          <button
            onClick={prevSection}
            className="p-3 bg-white rounded-xl shadow-card hover:shadow-lg transition-all duration-300 hover:bg-light-grey"
          >
            {isRTL ? (
              <ChevronRight className="w-6 h-6 text-primary" />
            ) : (
              <ChevronLeft className="w-6 h-6 text-primary" />
            )}
          </button>

          <div className="flex gap-2">
            {sections.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveSection(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === activeSection
                    ? "bg-primary scale-125"
                    : "bg-primary/20"
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSection}
            className="p-3 bg-white rounded-xl shadow-card hover:shadow-lg transition-all duration-300 hover:bg-light-grey"
          >
            {isRTL ? (
              <ChevronLeft className="w-6 h-6 text-primary" />
            ) : (
              <ChevronRight className="w-6 h-6 text-primary" />
            )}
          </button>
        </div>
      </div>

      {/* Bottom Banner */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto [text-align:start] mt-20 bg-gradient-to-br from-dark-grey via-surface-dark to-primary-container-dark text-white p-10 rounded-xl shadow-2xl relative overflow-hidden"
      >
        <div className="absolute top-0 end-0 w-32 h-32 bg-white/10 rounded-full rtl:-translate-x-16 ltr:translate-x-16 -translate-y-16"></div>
        <div className="absolute bottom-0 start-0 w-48 h-48 bg-primary/20 rounded-full rtl:translate-x-24 ltr:-translate-x-24 translate-y-24"></div>

        <div className="relative z-10">
          <div className="flex items-center justify-end gap-4 mb-6">
            <Users className="w-8 h-8 text-medium-blue" />
            <h3 className="text-3xl font-bold">{t("privacy.bannerTitle")}</h3>
          </div>

          <p className="text-white/70 leading-relaxed text-lg mb-6">
            {t("privacy.bannerDesc")}
          </p>

          <div className="flex flex-row-reverse gap-4">
            <a
              href={`tel:${CONTACT_PHONE}`}
              className="bg-white text-primary px-8 py-3 rounded-xl font-semibold hover:bg-light-grey transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {t("privacy.contactUs")}
            </a>
            <button
              onClick={downloadPolicy}
              disabled={isGeneratingPdf}
              className="border-2 border-white text-white px-8 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isGeneratingPdf
                ? t("privacy.generatingPolicy", "...")
                : t("privacy.downloadPolicy")}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Hidden printable content used to render the downloadable PDF */}
      <div
        style={{ position: "fixed", top: 0, left: "-9999px", width: "800px" }}
        aria-hidden="true"
      >
        <div
          ref={printableRef}
          dir={isRTL ? "rtl" : "ltr"}
          style={{
            width: "800px",
            padding: "48px",
            backgroundColor: "#ffffff",
            color: "#111827",
            fontFamily:
              "'Segoe UI', Tahoma, Geneva, Arial, sans-serif",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          <h1 style={{ fontSize: "28px", fontWeight: 700, marginBottom: "24px" }}>
            {t("privacy.title")}
          </h1>
          {sections.map((section, index) => (
            <div key={index} style={{ marginBottom: "24px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>
                {section.title}
              </h2>
              <p style={{ fontSize: "13px", lineHeight: 1.8, whiteSpace: "pre-line" }}>
                {section.content}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="text-center mt-20 text-grey"
      >
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-2 h-2 bg-success rounded-full"></div>
          <p className="text-sm">{t("privacy.footerNote")}</p>
        </div>
        <p className="text-lg font-semibold text-dark-grey">
          © {new Date().getFullYear()} {t("privacy.subtitleBrand")}.{" "}
          {t("privacy.footerRights")}.
        </p>
      </motion.footer>
    </div>
  );
}
